import { useMemo } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowUpRight, Wallet as WalletIcon } from "lucide-react";
import AuditableFigure from "../components/common/AuditableFigure";
import ResponseBadge from "../components/opportunity/ResponseBadge";
import CommercialVisionSection from "../components/home/CommercialVisionSection";
import { useEngines, useClosures, useTeam, usePortfolioSize, useOpportunities } from "../hooks/data";
import { ENGINES } from "../config/engines";
import { pickNextConversation } from "../lib/priority";
import { fmtMoney, fmtNumber } from "../lib/format";
import { FOUNDER_FIRST_NAME } from "../config/vertical";
import { T } from "../lib/testids";

function getGreeting() {
  const h = new Date().getHours();
  if (h < 12) return "Buenos días";
  if (h < 19) return "Buenas tardes";
  return "Buenas noches";
}

function getTodayLabel() {
  const label = new Date().toLocaleDateString("es-MX", {
    weekday: "long",
    day: "numeric",
    month: "long",
  });
  return label.charAt(0).toUpperCase() + label.slice(1);
}

// Iniciales para el retrato del contacto -- misma idea que cualquier avatar
// de persona: dos letras, nunca un ícono genérico de "empresa".
function initialsOf(name) {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  return parts
    .slice(0, 2)
    .map((p) => p[0])
    .join("")
    .toUpperCase();
}

function firstNameOf(name) {
  if (!name) return "";
  return name.trim().split(/\s+/)[0];
}

export default function Home() {
  const navigate = useNavigate();
  const { data: engines } = useEngines();
  const { data: closures } = useClosures(30);
  const { data: team } = useTeam();
  const { data: portfolioSize } = usePortfolioSize();
  const { data: allOpportunities } = useOpportunities({ limit: 5000 });

  const liveSummaries = useMemo(
    () => (engines || []).filter((e) => e.status === "live" && e.potentialValue),
    [engines]
  );

  // Aggregate estimate across every LIVE category — same mechanism as
  // before (AuditableFigure), never a plain number.
  const aggregate = useMemo(() => {
    if (liveSummaries.length === 0) return null;
    const total = liveSummaries.reduce((s, e) => s + e.potentialValue.value.amount, 0);
    const weightedConf =
      liveSummaries.reduce(
        (s, e) => s + e.potentialValue.confidence * e.potentialValue.value.amount,
        0
      ) / Math.max(total, 1);
    const sampleTotal = liveSummaries.reduce((s, e) => s + e.potentialValue.sampleSize, 0);
    return {
      label: "potential_business",
      value: { amount: total, currency: "USD" },
      confidence: weightedConf,
      sampleSize: sampleTotal,
      errorMarginPct: 12,
      rationale:
        "Suma del valor comercial estimado en cada categoría activa de tu cartera. Cada una documenta su propia metodología (click en el ícono para ver la evidencia).",
      lastRecalculatedAt: new Date().toISOString(),
    };
  }, [liveSummaries]);

  const me = team?.find((m) => m.id === "user-me");
  const meClosuresCount = closures?.filter((c) => c.closedBy === "user-me").length || 0;
  const closedThisMonth = closures?.reduce((s, c) => s + c.closedValue.amount, 0) || 0;

  // "Tu ritmo actual" ya no se muestra como cifra plana (era una violación
  // de la regla de auditabilidad, detectada en AUDIT_V0.01.md D7). Es una
  // proyección simple y se presenta como tal, con margen de error honesto.
  const paceEstimate = useMemo(() => {
    if (!me) return null;
    return {
      label: "potential_business",
      value: { amount: (me.monthClosedValue.amount || 0) * 3, currency: "USD" },
      confidence: 0.5,
      sampleSize: meClosuresCount,
      errorMarginPct: 35,
      rationale:
        "Proyección simple: tu resultado de este mes hasta ahora, multiplicado por 3 (extrapolación a 90 días). No usa tu historial de meses anteriores — por eso el margen de error es amplio.",
      lastRecalculatedAt: new Date().toISOString(),
    };
  }, [me, meClosuresCount]);

  // "A quién llamo primero" — una sola persona, nunca un conteo. El
  // criterio de negocio vive en lib/priority.js (única fuente de verdad,
  // reutilizable desde cualquier pantalla) para que Home no duplique lógica.
  const { next: nextConversation, rest: otherConversations } = useMemo(
    () => pickNextConversation(allOpportunities || []),
    [allOpportunities]
  );

  // "¿Qué encontró?" — el primer renglón del informe. Reutiliza los mismos
  // datos que ya se cargaron para la acción principal; no es una llamada
  // nueva, solo una cuenta sobre lo que ya está en memoria.
  const totalPendingCount = useMemo(
    () => (allOpportunities || []).filter((o) => o.outcome === "pending").length,
    [allOpportunities]
  );

  return (
    <div className="ambient-bg min-h-full">
      {/* Una historia, no una colección de tarjetas. Cuatro preguntas, en
          orden, sin nada de relleno entre ellas: qué encontré, cuánto dinero
          hay en juego, qué hago primero (la tarjeta héroe, imposible de
          ignorar) y qué hago después. El espacio en blanco separa, no las
          líneas. */}
      <div className="max-w-[960px] mx-auto px-5 sm:px-8 lg:px-10 pt-10 sm:pt-16 lg:pt-20 pb-20">
        <div className="text-[11px] text-zinc-500 font-mono uppercase tracking-[0.25em] mb-5">
          {getTodayLabel()}
        </div>

        <div
          data-testid={T.homeGreeting}
          className="animate-rise font-display font-black text-2xl sm:text-3xl lg:text-4xl text-zinc-50 leading-[1.15]"
        >
          {getGreeting()}{FOUNDER_FIRST_NAME ? `, ${FOUNDER_FIRST_NAME}` : ""}.
        </div>

        <div data-testid={T.homeBriefing} className="animate-rise max-w-xl mt-4">
          {portfolioSize != null && (
            <p className="text-base sm:text-lg text-zinc-300 leading-relaxed">
              Mientras no estabas, revisé tu cartera de {fmtNumber(portfolioSize)} clientes
              {totalPendingCount > 0 && (
                <>
                  {" "}y encontré{" "}
                  <span className="text-zinc-50 font-semibold">
                    {fmtNumber(totalPendingCount)} oportunidades reales
                  </span>
                </>
              )}
              .
            </p>
          )}
        </div>

        {/* Cuánto dinero hay en juego — el número con más peso visual de
            toda la pantalla, junto con la tarjeta héroe de abajo. */}
        {aggregate && (
          <div className="animate-rise animate-rise-1 mt-9 sm:mt-11" data-testid={T.homeMoneyDial}>
            <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 font-mono mb-2">
              Hoy podrías recuperar
            </div>
            <AuditableFigure estimate={aggregate} size="xl" tone="amber" />
          </div>
        )}

        {/* Qué hago primero — la única superficie elevada de la pantalla.
            Todo lo demás vive sin caja para que esta sea imposible de
            ignorar. */}
        <div className="animate-rise animate-rise-2 mt-14 sm:mt-16">
          {nextConversation ? (
            <div
              data-testid={T.homeNextConversation}
              className="hero-glow rounded-[28px] bg-gradient-to-br from-[#161d33] to-[#0e1424] shadow-[0_28px_70px_-24px_rgba(0,0,0,0.65)] p-6 sm:p-9 md:p-10 flex flex-col md:flex-row md:items-center gap-7 md:gap-10"
            >
              <div className="hidden sm:flex w-16 h-16 md:w-20 md:h-20 rounded-full bg-amber-500/12 text-amber-400 items-center justify-center font-display font-black text-xl md:text-2xl shrink-0">
                {initialsOf(nextConversation.companyName)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[11px] uppercase tracking-[0.3em] text-amber-400 font-mono font-semibold mb-3">
                  Empezaría por aquí
                </div>
                {/* Defecto real encontrado en pruebas destructivas: un nombre
                    de empresa extremadamente largo producía un muro de texto
                    envuelto en 10+ líneas — truncate + title da un resultado
                    limpio en vez de "esto parece roto". */}
                <div
                  className="font-display font-black text-2xl sm:text-3xl md:text-4xl text-white leading-tight truncate"
                  title={nextConversation.companyName}
                >
                  {nextConversation.companyName}
                </div>
                <div className="text-sm text-zinc-400 mt-1.5 truncate">{nextConversation.contactName}</div>
                <div className="mt-3">
                  <ResponseBadge status={nextConversation.responseStatus} />
                </div>
                <p className="text-[15px] text-zinc-300 mt-3 max-w-lg leading-relaxed">
                  {nextConversation.priorityReason}
                </p>
              </div>
              <button
                data-testid={T.homeNextConversationCta}
                onClick={() => navigate(`/wallet?opp=${nextConversation.id}`)}
                className="w-full md:w-auto shrink-0 inline-flex items-center justify-center gap-2 text-base font-bold px-8 py-4 rounded-full bg-amber-500 text-black hover:bg-amber-400 btn-glow-amber transition-transform hover:-translate-y-0.5"
              >
                Hablar con {firstNameOf(nextConversation.contactName) || "el cliente"} <ArrowUpRight size={18} />
              </button>
            </div>
          ) : (
            <div className="hero-glow rounded-[28px] bg-gradient-to-br from-[#161d33] to-[#0e1424] shadow-[0_28px_70px_-24px_rgba(0,0,0,0.65)] p-6 sm:p-9 md:p-10 flex flex-col md:flex-row md:items-center justify-between gap-7 md:gap-10">
              <div className="min-w-0">
                <div className="text-[11px] uppercase tracking-[0.3em] text-amber-400 font-mono font-semibold mb-3">
                  Primer paso
                </div>
                <div className="font-display font-black text-2xl sm:text-3xl text-white leading-tight">
                  Carga tu cartera y te digo por dónde empezar
                </div>
                <p className="text-[15px] text-zinc-300 mt-2 max-w-lg leading-relaxed">
                  Carga tus clientes y Commercial Operator empieza a buscar oportunidades reales.
                </p>
              </div>
              <button
                onClick={() => navigate("/ingestion")}
                className="w-full md:w-auto shrink-0 inline-flex items-center justify-center gap-2 text-base font-bold px-8 py-4 rounded-full bg-amber-500 text-black hover:bg-amber-400 btn-glow-amber transition-transform hover:-translate-y-0.5"
              >
                Cargar cartera <ArrowUpRight size={18} />
              </button>
            </div>
          )}
        </div>

        {/* Qué hago después — deliberadamente en voz baja frente al héroe:
            texto plano, sin caja, separado solo por líneas finas. */}
        {otherConversations.length > 0 && (
          <div className="mt-12 sm:mt-14" data-testid={T.homeOtherConversations}>
            <div className="text-[11px] uppercase tracking-[0.22em] text-zinc-500 font-mono mb-3">
              Después, seguiría con
            </div>
            <div className="divide-y divide-white/[0.06]">
              {otherConversations.slice(0, 5).map((o) => (
                <button
                  key={o.id}
                  data-testid={T.homeOtherConversationRow(o.id)}
                  onClick={() => navigate(`/wallet?opp=${o.id}`)}
                  className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between gap-1 sm:gap-4 py-4 -mx-3 px-3 rounded-xl text-left hover:bg-white/[0.03] transition-colors"
                >
                  <div className="min-w-0">
                    <div className="text-[15px] text-zinc-100 font-medium truncate">{o.companyName}</div>
                    <div className="text-xs text-zinc-500 mt-0.5">{o.contactName}</div>
                  </div>
                  <div className="text-xs text-zinc-500 sm:text-right max-w-full sm:max-w-[45%]">
                    {o.priorityReason}
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Accesos rápidos — enlaces de texto, nunca botones que compitan
            con la conversación principal. */}
        <div className="mt-8 flex flex-wrap items-center gap-x-7 gap-y-3">
          <Link
            to="/wallet"
            data-testid={T.homeWalletLink}
            className="inline-flex items-center gap-1.5 text-[13px] text-zinc-400 hover:text-amber-400 border-b border-transparent hover:border-amber-400 pb-0.5 transition-colors"
          >
            <WalletIcon size={12} /> Toda mi cartera
          </Link>
          {ENGINES.filter((e) => e.status !== "live").map((engine) => (
            <button
              key={engine.id}
              data-testid={T.homeActionSoon(engine.id)}
              onClick={() => toast.info(engine.anticipationText)}
              className="text-[13px] text-zinc-600 hover:text-zinc-400 border-b border-transparent hover:border-zinc-600 pb-0.5 transition-colors"
            >
              {engine.humanLabel}
            </button>
          ))}
        </div>

        {/* Resultados — apoyo silencioso al cierre, nunca compite con lo de
            arriba. Una sola línea de cifras, no tres tarjetas. */}
        <div
          className="mt-16 sm:mt-20 pt-8 border-t border-white/[0.07] flex flex-col sm:flex-row sm:divide-x sm:divide-white/[0.07]"
        >
          <div className="sm:pr-8" data-testid={T.homeCommission}>
            <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2">
              Dinero recuperado este mes
            </div>
            {closedThisMonth > 0 ? (
              <>
                <div className="font-display font-black text-2xl tabular-nums text-emerald-400">
                  {fmtMoney(closedThisMonth)}
                </div>
                <div className="text-[11px] text-zinc-500 mt-1.5">
                  {closures?.length || 0} ventas cerradas este mes
                </div>
              </>
            ) : (
              // Un "$0" real (no una estimación) en el primer día no es una
              // mala noticia — es honesto y además una invitación: todavía no
              // hay nada cerrado porque el mes recién empieza, no porque el
              // producto no sirva. Se lo decimos así, no con un cero seco.
              <>
                <div className="font-display font-bold text-base text-zinc-500 italic">
                  Todavía sin cierres este mes
                </div>
                <div className="text-[11px] text-zinc-600 mt-1.5">
                  Tu siguiente conversación está lista arriba
                </div>
              </>
            )}
          </div>
          <div className="sm:px-8 pt-6 sm:pt-0">
            <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2">
              Tu ritmo actual
            </div>
            {paceEstimate ? (
              <AuditableFigure estimate={paceEstimate} size="md" tone="neutral" />
            ) : (
              <div className="text-zinc-600 font-mono">—</div>
            )}
          </div>
          {team && team.length > 1 && (
            <div className="sm:pl-8 pt-6 sm:pt-0">
              <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2">
                Equipo activo esta semana
              </div>
              <div className="space-y-1.5 mt-1">
                {team.slice(0, 3).map((m) => (
                  <div key={m.id} className="flex items-center justify-between text-xs gap-6">
                    <span className="text-zinc-400">{m.name}</span>
                    <span className="font-mono text-zinc-200 tabular-nums">
                      {fmtMoney(m.monthClosedValue.amount)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Solo escritorio — visión de plataforma, después del trabajo del
          día. En móvil no se renderiza (ver CommercialVisionSection). */}
      <CommercialVisionSection />
    </div>
  );
}
